/**
 * Lightweight product / UX analytics for the web UI.
 * - Dispatches window event for third-party wiring
 * - POSTs to /analytics/ui-events (best-effort, keepalive)
 */

import { getAuthHeaders } from './auth.js'

const AUTH_ENTRYPOINT_KEY = 'agent_auth_entrypoint'
const WELCOME_SHOWN_SESSION_KEY = 'agent_tracked_welcome_splash_shown'

function getAnonClientId() {
  try {
    const k = 'agent_ui_anon_id'
    let id = localStorage.getItem(k)
    if (id && id.length >= 8) return id
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      id = crypto.randomUUID()
    } else {
      id = `${Date.now()}-${Math.random().toString(16).slice(2)}`
    }
    localStorage.setItem(k, id)
    return id
  } catch {
    return null
  }
}

function commonContext() {
  const ctx = {
    anon_client_id: getAnonClientId(),
    path: typeof window !== 'undefined' ? window.location.pathname : null,
    search: typeof window !== 'undefined' ? window.location.search : null,
  }
  try {
    if (typeof window !== 'undefined' && window.__BUILD_VERSION__) {
      ctx.build_version = window.__BUILD_VERSION__
    }
  } catch {
    // ignore
  }
  return ctx
}

/**
 * @param {string} event
 * @param {Record<string, string|number|boolean|null|undefined>} [properties]
 */
export function trackUiEvent(event, properties = {}) {
  if (!event || typeof event !== 'string') return

  const payload = {
    event: event.slice(0, 120),
    properties: { ...commonContext(), ...properties },
    client_ts_ms: Date.now(),
  }

  try {
    window.dispatchEvent(new CustomEvent('captainCookUiTelemetry', { detail: payload }))
  } catch {
    // ignore
  }

  if (typeof window.__CAPTAIN_COOK_UI_TRACK__ === 'function') {
    try {
      window.__CAPTAIN_COOK_UI_TRACK__(payload)
    } catch {
      // ignore
    }
  }

  try {
    const body = JSON.stringify(payload)
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' }
    fetch('/analytics/ui-events', {
      method: 'POST',
      headers,
      body,
      keepalive: true,
    }).catch(() => {})
  } catch {
    // ignore
  }
}

export function setAuthEntrypoint(value) {
  try {
    if (value) sessionStorage.setItem(AUTH_ENTRYPOINT_KEY, String(value).slice(0, 64))
    else sessionStorage.removeItem(AUTH_ENTRYPOINT_KEY)
  } catch {
    // ignore
  }
}

/** Sets entrypoint only if none is already set (first touch wins until consumeAuthEntrypoint). */
export function ensureAuthEntrypoint(value) {
  try {
    if (!sessionStorage.getItem(AUTH_ENTRYPOINT_KEY)) {
      setAuthEntrypoint(value)
    }
  } catch {
    // ignore
  }
}

export function consumeAuthEntrypoint() {
  try {
    const v = sessionStorage.getItem(AUTH_ENTRYPOINT_KEY)
    sessionStorage.removeItem(AUTH_ENTRYPOINT_KEY)
    return v || 'unknown'
  } catch {
    return 'unknown'
  }
}

/** One "shown" event per browser tab session (avoids double-count in React Strict Mode dev). */
export function trackWelcomeSplashShownOnce(properties = {}) {
  try {
    if (sessionStorage.getItem(WELCOME_SHOWN_SESSION_KEY) === '1') return
    sessionStorage.setItem(WELCOME_SHOWN_SESSION_KEY, '1')
  } catch {
    // ignore
  }
  trackUiEvent('welcome_splash_shown', properties)
}
